import React, { useState, useEffect } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
	PlusIcon,
	XIcon,
	PhotographIcon,
	CheckCircleIcon,
	MailIcon,
	TrashIcon,
	LocationMarkerIcon,
	GlobeAltIcon,
	UserCircleIcon,
	SaveIcon,
	CheckIcon,
	HomeIcon,
	SparklesIcon,
	ChatAlt2Icon,
	ClockIcon
} from '@heroicons/react/outline'
import connexion from '../../connexion'
import tools from '../../tools'
import fr_flag from '../../images/flags/france.png'
import en_flag from '../../images/flags/united-kingdom.png'
import de_flag from '../../images/flags/germany.png'
import nl_flag from '../../images/flags/netherlands.png'
import it_flag from '../../images/flags/italy.png'
import es_flag from '../../images/flags/spain.png'
import pt_flag from '../../images/flags/portugal.png'
import Modal from '../UI/Modal'
import Loader from '../UI/Loader'
import Payments from './Payments'

const LENGTH_MAX = 40

const LANGS_META = [
	{ code: 'fr', src: fr_flag, label: 'Français' },
	{ code: 'en', src: en_flag, label: 'Anglais' },
	{ code: 'de', src: de_flag, label: 'Allemand' },
	{ code: 'nl', src: nl_flag, label: 'Néerlandais' },
	{ code: 'it', src: it_flag, label: 'Italien' },
	{ code: 'es', src: es_flag, label: 'Espagnol' },
	{ code: 'pt', src: pt_flag, label: 'Portugais' },
]

function initErrors()
{
	return { global: null, establishment: null, welcome: null, nbdays: null, langs: null }
}

function parseEmailsList(str)
{
	if (!str)
		return [{ offset: 7, unit: 'days' }]
	const days = str.split(',').map(d => d.trim()).filter(d => d !== '')
	if (days.length === 0)
		return [{ offset: 7, unit: 'days' }]
	return days.map(d => ({ offset: parseInt(d) || 1, unit: 'days' }))
}

function emailToDays(e)
{
	const n = parseInt(e.offset) || 1
	switch (e.unit)
	{
		case 'hours': return Math.max(1, Math.round(n / 24))
		case 'weeks': return n * 7
		case 'months': return n * 30
		default: return n
	}
}

function Toggle({ checked, onChange, disabled })
{
	return (
		<div
			onClick={disabled ? undefined : onChange}
			style={{ cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0 }}
		>
			<div
				style={{
					width: '2.75rem',
					height: '1.5rem',
					borderRadius: '9999px',
					backgroundColor: checked ? '#2563eb' : '#d1d5db',
					transition: 'background-color 0.2s',
					position: 'relative',
					opacity: disabled ? 0.6 : 1,
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: '2px',
						left: checked ? 'calc(100% - 1.25rem - 2px)' : '2px',
						width: '1.25rem',
						height: '1.25rem',
						borderRadius: '50%',
						backgroundColor: '#fff',
						transition: 'left 0.2s',
						boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
					}}
				/>
			</div>
		</div>
	)
}

function ConfigWebapp(props)
{
	const [establishment, setEstablishment] = useState(props.establishment || '')
	const [activeLangs, setActiveLangs] = useState({
		fr: props.langs.includes('fr'),
		en: props.langs.includes('en'),
		de: props.langs.includes('de'),
		nl: props.langs.includes('nl'),
		it: props.langs.includes('it'),
		es: props.langs.includes('es'),
		pt: props.langs.includes('pt'),
	})
	const [welcome, setWelcome] = useState(tools.clone(props.welcome_txt || []))
	const [sendmail, setSendmail] = useState(!!props.emails_activate)
	const [emailsList, setEmailsList] = useState(() => parseEmailsList(props.emails_nbdays))
	const [background, setBackground] = useState(props.background_img || null)
	const [currentLang, setCurrentLang] = useState(props.lang_default)
	const [messagerieActive, setMessagerieActive] = useState(() => {
		const dc = props.dashboard_config || {}
		return !!(dc.messages && dc.messages.active === true)
	})

	const [homeBanner, setHomeBanner] = useState(null)
	const [homeBannerPending, setHomeBannerPending] = useState(null)
	// Horaires d'arrivée / départ par type d'hébergement (admin_config global/stay-hours)
	const [stayHours, setStayHours] = useState({ locatif: { arrival: '', departure: '' }, emplacement: { arrival: '', departure: '' }, late_arrival: '' })
	const [stayHoursState, setStayHoursState] = useState(null) // null | 'saving' | 'saved' | 'error'

	useEffect(() => {
		fetch(connexion.connect.url + 'dashboard/config/stay-hours')
			.then(res => res.json())
			.then(value => {
				if (!value)
					return
				const toStr = (v) => (v === null || v === undefined) ? '' : String(v)
				setStayHours({
					locatif: { arrival: toStr(value.locatif && value.locatif.arrival), departure: toStr(value.locatif && value.locatif.departure) },
					emplacement: { arrival: toStr(value.emplacement && value.emplacement.arrival), departure: toStr(value.emplacement && value.emplacement.departure) },
					late_arrival: toStr(value.late_arrival)
				})
			})
			.catch(() => {})
	}, [])

	const saveStayHours = (next) => {
		setStayHours(next)
		setStayHoursState('saving')
		fetch(connexion.connect.url + 'dashboard/config/stay-hours/update', {
			method: 'POST',
			body: JSON.stringify(next)
		})
			.then(res => res.json())
			.then(ret => setStayHoursState(ret === false ? 'error' : 'saved'))
			.catch(() => setStayHoursState('error'))
	}

	const handleStayHourChange = (group, field, raw) => {
		if (raw !== '' && !(Number.isInteger(+raw) && +raw >= 0 && +raw <= 23))
			return
		saveStayHours({ ...stayHours, [group]: { ...stayHours[group], [field]: raw } })
	}

	const handleLateArrivalChange = (raw) => {
		if (raw !== '' && !(Number.isInteger(+raw) && +raw >= 0 && +raw <= 23))
			return
		saveStayHours({ ...stayHours, late_arrival: raw })
	}
	const [homeBannerLoading, setHomeBannerLoading] = useState(false)
	const [homeBannerError, setHomeBannerError] = useState(null)
	const [homeBannerSuccess, setHomeBannerSuccess] = useState(null)
	const [homeBannerDragOver, setHomeBannerDragOver] = useState(false)

	const [attemptSubmit, setAttemptSubmit] = useState(false)
	const [errors, setErrors] = useState(initErrors())
	const [successMsg, setSuccessMsg] = useState(null)
	const [modalMsg, setModalMsg] = useState(null)
	const [openModal, setOpenModal] = useState(false)

	useEffect(() => {
		props.updateNavbarTitle('Paramètres généraux')
		fetch(connexion.connect.url + 'webapp/config/home-banner')
			.then(res => res.json())
			.then(banner => setHomeBanner(banner || null))
			.catch(() => {})
	}, [])

	useEffect(() => {
		const dc = props.dashboard_config || {}
		setMessagerieActive(!!(dc.messages && dc.messages.active === true))
	}, [props.dashboard_config])

	const permissions = props.user.permissions
	if (!permissions || !permissions.params || permissions.params === 0)
		return <Redirect to="/" />

	const isSuper = !!(permissions.super && permissions.super % 2 === 1)
	const isAdmin = !!(permissions.admin && permissions.admin % 2 === 1)
	const isPremium = props.features === 'premium'
	const [appOuverte, setAppOuverte] = useState(props.application_ouverte !== false)

	// --- Handlers ---

	const handleToggleLang = (code) => {
		const isCurrentlyActive = activeLangs[code]
		setActiveLangs(prev => ({ ...prev, [code]: !prev[code] }))
		if (isCurrentlyActive && currentLang === code)
			setCurrentLang(props.lang_default)
	}

	const handleWelcome = (value) => {
		setWelcome(prev => {
			const updated = [...prev]
			const idx = updated.findIndex(w => w.lang === currentLang)
			if (idx !== -1)
				updated[idx] = { ...updated[idx], welcome: value }
			else
				updated.push({ lang: currentLang, welcome: value })
			return updated
		})
	}

	const getCurrentWelcome = () => {
		const entry = welcome.find(w => w.lang === currentLang)
		return entry ? entry.welcome : ''
	}

	const addEmail = () => {
		if (emailsList.length >= 10)
			return ;
		setEmailsList(prev => [...prev, { offset: 1, unit: 'days' }])
	}

	const removeEmail = (index) => {
		setEmailsList(prev => prev.filter((_, i) => i !== index))
	}

	const updateEmail = (index, field, value) => {
		setEmailsList(prev => {
			const updated = [...prev]
			updated[index] = { ...updated[index], [field]: value }
			return updated
		})
	}

	const handleBackground = (event) => {
		const file = event.target.files[0]
		if (!file)
			return ;
		const reader = new FileReader()
		reader.addEventListener('load', () => setBackground(reader.result))
		reader.readAsDataURL(file)
	}

	const handleHomeBanner = (e) => {
		const file = e.target.files[0]
		if (!file)
			return ;
		setHomeBannerError(null)
		setHomeBannerSuccess(null)
		tools.resizeImage(file, 1600, 400, 0.85)
			.then(data => setHomeBannerPending(data))
			.catch(err => setHomeBannerError(err))
	}

	const handleBannerDrop = (e) => {
		e.preventDefault()
		setHomeBannerDragOver(false)
		const file = e.dataTransfer.files[0]
		if (!file)
			return ;
		setHomeBannerError(null)
		setHomeBannerSuccess(null)
		tools.resizeImage(file, 1600, 400, 0.85)
			.then(data => setHomeBannerPending(data))
			.catch(err => setHomeBannerError(err))
	}

	const doSaveHomeBanner = () => {
		setHomeBannerLoading(true)
		setHomeBannerError(null)
		setHomeBannerSuccess(null)
		props.insertHomeBanner(homeBannerPending)
			.then(file_id => {
				setHomeBanner(file_id)
				setHomeBannerPending(null)
				setHomeBannerLoading(false)
				setHomeBannerSuccess('Bannière enregistrée.')
			})
			.catch(() => {
				setHomeBannerLoading(false)
				setHomeBannerError("Erreur lors de l'enregistrement.")
			})
	}

	const doRemoveHomeBanner = () => {
		setHomeBannerLoading(true)
		setHomeBannerError(null)
		setHomeBannerSuccess(null)
		props.removeHomeBanner()
			.then(() => {
				setHomeBanner(null)
				setHomeBannerPending(null)
				setHomeBannerLoading(false)
				setHomeBannerSuccess('Bannière supprimée.')
			})
			.catch(() => {
				setHomeBannerLoading(false)
				setHomeBannerError('Erreur lors de la suppression.')
			})
	}

	const validateForm = () => {
		const errs = initErrors()
		let hasError = false

		if (establishment.length === 0)
		{
			hasError = true
			errs.establishment = "Veuillez renseigner le nom de l'établissement"
		}
		else if (establishment.length > LENGTH_MAX)
		{
			hasError = true
			errs.establishment = "Le nom de l'établissement ne doit pas dépasser " + LENGTH_MAX + " caractères"
		}

		if (emailsList.length === 0)
		{
			hasError = true
			errs.nbdays = "Veuillez ajouter au moins une relance"
		}

		if (!activeLangs[props.lang_default])
		{
			hasError = true
			errs.langs = "Vous ne pouvez pas désactiver cette langue : " + props.lang_default.toUpperCase()
		}

		const defaultEntry = welcome.find(w => w.lang === props.lang_default)
		if (!defaultEntry || defaultEntry.welcome.length === 0)
		{
			hasError = true
			errs.welcome = "Vous devez indiquer le texte de bienvenue (version " + props.lang_default.toUpperCase() + ")"
		}
		else
		{
			for (const entry of welcome)
			{
				if (entry.welcome.length > LENGTH_MAX)
				{
					hasError = true
					errs.welcome = "Le texte de bienvenue ne doit pas dépasser " + LENGTH_MAX + " caractères (version " + entry.lang.toUpperCase() + ")"
					break
				}
			}
		}

		if (hasError)
		{
			errs.global = "Il y a une ou plusieurs erreur(s) dans le formulaire"
			setErrors(errs)
			setSuccessMsg(null)
			setAttemptSubmit(false)
		}
		return !hasError
	}

	const doUpdateConfig = () => {
		const config = {
			establishment,
			sendmail,
			nbdays: emailsList.map(emailToDays).join(','),
			langs: {
				fr: activeLangs.fr,
				en: activeLangs.en,
				de: activeLangs.de,
				nl: activeLangs.nl,
				it: activeLangs.it,
				es: activeLangs.es,
				pt: activeLangs.pt,
			},
			background,
			welcome,
		}
		props.updateConfig(config)
			.then(ret => {
				setAttemptSubmit(false)
				if (!ret)
				{
					setErrors({ ...initErrors(), global: "Erreur: Impossible de mettre à jour les paramètres généraux. Veuillez contacter le service technique (code : #1)" })
					setSuccessMsg(null)
				}
				else
				{
					setErrors(initErrors())
					setSuccessMsg('Les paramètres ont bien été mis à jour')
				}
			})
			.catch(err => {
				setAttemptSubmit(false)
				setErrors({ ...initErrors(), global: "Erreur: Impossible de mettre à jour les paramètres généraux. Veuillez contacter le service technique (code : #2)" })
				setSuccessMsg(null)
				console.log(err)
			})
	}

	const handleSubmit = (event) => {
		event.preventDefault()
		setAttemptSubmit(true)
		if (!validateForm())
			return ;

		const activeLangCodes = Object.keys(activeLangs).filter(code => activeLangs[code])
		const missingLangs = activeLangCodes.filter(code => {
			const entry = welcome.find(w => w.lang === code)
			return !entry || entry.welcome.length === 0
		})
		if (missingLangs.length > 0)
		{
			const msg = "Le texte de bienvenue n'est pas traduit dans les langues suivantes : " + missingLangs.map(l => l.toUpperCase()).join(', ')
			setModalMsg(msg)
			setOpenModal(true)
		}
		else
			doUpdateConfig()
	}

	const answerModal = (answer) => {
		if (answer === 'yes')
			doUpdateConfig()
		else
		{
			setAttemptSubmit(false)
			setOpenModal(false)
		}
	}

	// Shared style tokens
	const inputClass = "browser-default w-full border border-slate-200 rounded-lg px-4 py-3 text-sm bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
	const labelClass = "block text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-widest italic"
	const cardHeaderClass = "px-5 py-4 border-b border-slate-100 flex items-center bg-slate-50/30"
	const cardClass = "bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
	const currentLangMeta = LANGS_META.find(l => l.code === currentLang) || LANGS_META[0]

	return (
		<div className="wrapper-content">
			<Modal
				dismissible={false}
				content={modalMsg}
				open_modal={openModal}
				answerModal={answerModal}
			/>

			<form onSubmit={handleSubmit}>
				{/* Breadcrumb */}
				<div className="mb-5">
					<ol className="flex items-center gap-1 text-sm">
						<li className="flex items-center">
							<Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
								<HomeIcon className="flex-shrink-0 h-4 w-4" aria-hidden="true" />
								<span className="sr-only">Accueil</span>
							</Link>
						</li>
						<li className="flex items-center">
							<span className="text-gray-300 mx-1 select-none">/</span>
							<span className="text-gray-400">Paramètres généraux</span>
						</li>
					</ol>
				</div>

				{/* Page header */}
				<div className="flex items-end justify-between mb-8">
					<div>
						<h1
							style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-0.02em', margin: 0 }}
							className="text-4xl font-bold text-gray-900 leading-none"
						>
							Paramètres généraux
						</h1>
						<p className="text-sm text-gray-400" style={{ margin: '0.5rem 0 0' }}>
							Relances e-mails avant séjour, langues et configuration de votre application
						</p>
						{successMsg && (
							<motion.p
								initial={{ opacity: 0, y: -4 }}
								animate={{ opacity: 1, y: 0 }}
								className="flex items-center gap-1 text-xs text-emerald-600 font-medium mt-0.5"
							>
								<CheckCircleIcon className="w-3.5 h-3.5" />
								{successMsg}
							</motion.p>
						)}
						{errors.global && (
							<p className="text-xs text-red-500 font-medium mt-0.5">{errors.global}</p>
						)}
					</div>
					<div className="flex items-center gap-4">
						{isSuper && (
							<Link to="/administration/features" className="text-xs text-slate-400 hover:text-slate-600 font-medium">
								Paramètres admin
							</Link>
						)}
						<Link to="/emplacements/parametrage" className="text-xs text-slate-400 hover:text-slate-600 font-medium">
							Emplacements
						</Link>
						{isAdmin && (
							<Link to="/administration/utilisateurs" className="text-xs text-slate-400 hover:text-slate-600 font-medium">
								Équipe & accès
							</Link>
						)}
						<button
							type="submit"
							disabled={attemptSubmit}
							style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
							className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-bold rounded-xl shadow-sm disabled:opacity-50"
						>
							{attemptSubmit
								? <Loader />
								: <>
									<SaveIcon className="w-4 h-4" />
									Enregistrer
								</>
							}
						</button>
					</div>
				</div>

				{/* 2-column grid */}
				<div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1fr' }}>

					{/* ── Left column ── */}
					<div className="space-y-6">

						{/* Card: Identité */}
						<section className={cardClass}>
							<div className={cardHeaderClass}>
								<LocationMarkerIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
								<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Identité de l'établissement</h2>
							</div>
							<div className="p-6">
								<label style={{ margin: 0 }}>
									<span className={labelClass}>Nom de l'établissement</span>
									<input
										type="text"
										className={inputClass}
										value={establishment}
										onChange={e => setEstablishment(e.target.value)}
										placeholder="Ex: Camping l'Escale Verte"
										maxLength={LENGTH_MAX + 10}
									/>
									<div className="flex justify-between mt-1.5">
										{errors.establishment
											? <span className="text-[11px] text-red-500 font-medium">{errors.establishment}</span>
											: <span />
										}
										<span className={`text-[11px] ${establishment.length > LENGTH_MAX ? 'text-red-500' : 'text-slate-300'}`}>
											{establishment.length}/{LENGTH_MAX}
										</span>
									</div>
								</label>
							</div>
						</section>

						{/* Card: Langues */}
						<section className={cardClass}>
							<div className={cardHeaderClass}>
								<GlobeAltIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
								<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Langues activées dans l'app</h2>
							</div>
							<div className="p-6">
								{errors.langs && (
									<p className="text-[11px] text-red-500 font-medium mb-3">{errors.langs}</p>
								)}
								<div className="grid grid-cols-7 gap-1">
									{LANGS_META.map(({ code, src, label }) => {
										const isDefault = code === props.lang_default
										const isActive = activeLangs[code]
										return (
											<label
												key={code}
												className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg transition-all select-none ${isActive ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'} ${isDefault ? 'cursor-default' : 'cursor-pointer'}`}
												style={{ margin: 0 }}
											>
												<div
													className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${isActive ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-white'}`}
												>
													{isActive && <CheckIcon className="w-2.5 h-2.5 text-white" />}
												</div>
												<input
													type="checkbox"
													checked={isActive}
													disabled={isDefault}
													onChange={() => handleToggleLang(code)}
													className="hidden"
												/>
												<img src={src} alt={label} className="w-5 h-3.5 object-cover rounded-sm flex-shrink-0" />
												<span className="text-xs font-bold text-slate-700">{code.toUpperCase()}</span>
											</label>
										)
									})}
								</div>
								<p className="text-[10px] text-slate-400 italic mt-3">
									La langue par défaut ({props.lang_default.toUpperCase()}) ne peut pas être désactivée.
								</p>
							</div>
						</section>

						{/* Card: Relances */}
						<section className={cardClass}>
							<div className={cardHeaderClass}>
								<MailIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
								<div>
									<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-tight" style={{ margin: 0 }}>Relances automatiques (Emails)</h2>
									<p className="text-sm text-slate-400 font-normal" style={{ margin: 0 }}>Configurez vos relances e-mail envoyées avant l'arrivée des campeurs.</p>
								</div>
							</div>
							<div className="px-6 pt-5 pb-2">
								{/* Toggle relances */}
								<div className="flex items-center justify-between py-3 px-4 rounded-xl" style={{ backgroundColor: sendmail ? '#f0fdf4' : '#f8fafc', border: `1px solid ${sendmail ? '#bbf7d0' : '#e2e8f0'}` }}>
									<div>
										<p className="text-sm font-bold" style={{ margin: 0, color: sendmail ? '#166534' : '#64748b' }}>
											{sendmail ? 'Relances activées' : 'Relances désactivées'}
										</p>
										<p className="text-xs" style={{ margin: '0.2rem 0 0', color: sendmail ? '#15803d' : '#94a3b8' }}>
											{sendmail
												? 'Un e-mail est envoyé automatiquement aux campeurs avant leur séjour.'
												: 'Aucun e-mail automatique ne sera envoyé avant les séjours.'
											}
										</p>
									</div>
									<Toggle checked={sendmail} onChange={() => setSendmail(v => !v)} />
								</div>
							</div>
							<div className="p-6 flex flex-col gap-4">
								{errors.nbdays && (
									<p className="text-[11px] text-red-500 font-medium">{errors.nbdays}</p>
								)}
								{emailsList.map((email, index) => (
									<div key={index} className="flex items-center gap-3 group">
										<div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 font-black text-xs flex-shrink-0">
											#{index + 1}
										</div>
										<div className="flex-1 flex gap-2">
											<input
												type="number"
												value={email.offset}
												onChange={e => updateEmail(index, 'offset', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
												className="browser-default w-20 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold focus:outline-none focus:border-blue-400 transition-all"
											/>
											<select
												value={email.unit}
												onChange={e => updateEmail(index, 'unit', e.target.value)}
												className="browser-default flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold focus:outline-none focus:border-blue-400 transition-all"
											>
												<option value="hours">heures avant le séjour</option>
												<option value="days">jours avant le séjour</option>
												<option value="weeks">semaines avant le séjour</option>
												<option value="months">mois avant le séjour</option>
											</select>
										</div>
										{emailsList.length > 1 && (
											<button
												type="button"
												onClick={() => removeEmail(index)}
												className="p-2.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
												style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										)}
									</div>
								))}
								{emailsList.length < 10 && (
									<button
										type="button"
										onClick={addEmail}
										className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 rounded-xl text-xs font-bold text-slate-400 hover:text-blue-500 transition-all"
										style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
									>
										<PlusIcon className="h-4 w-4" />
										Ajouter une relance
									</button>
								)}
							</div>
						</section>
					</div>

					{/* ── Right column ── */}
					<div className="space-y-6">

						{/* Card: Bannière accueil */}
						<section className={cardClass}>
							<div className={cardHeaderClass}>
								<PhotographIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
								<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Bannière accueil</h2>
							</div>
							<div className="p-6">
								{/* Pending preview */}
								{homeBannerPending && (
									<div className="relative mb-4">
										<img
											src={homeBannerPending}
											alt="aperçu bannière"
											className="w-full object-cover rounded-lg"
											style={{ height: '9rem' }}
										/>
										<button
											type="button"
											onClick={() => setHomeBannerPending(null)}
											className="absolute top-2 right-2 bg-white rounded-full flex items-center justify-center shadow w-7 h-7"
											style={{ backgroundImage: 'none' }}
										>
											<XIcon className="w-4 h-4 text-slate-500" />
										</button>
									</div>
								)}

								{/* Saved banner */}
								{!homeBannerPending && homeBanner && (
									<div
										className="relative mb-4 rounded-lg overflow-hidden"
										style={homeBannerDragOver ? { outline: '3px solid #2563eb' } : {}}
										onDragOver={e => e.preventDefault()}
										onDragEnter={e => { e.preventDefault(); setHomeBannerDragOver(true) }}
										onDragLeave={() => setHomeBannerDragOver(false)}
										onDrop={handleBannerDrop}
									>
										<img
											src={connexion.connect.url + 'dashboard/files/get-image/' + homeBanner}
											alt="bannière actuelle"
											className="w-full object-cover"
											style={{ height: '9rem' }}
										/>
										<label
											className="absolute bottom-2 right-2 bg-white rounded-lg shadow px-3 py-1 text-xs font-bold cursor-pointer hover:bg-slate-50 transition-colors text-slate-700"
										>
											Changer
											<input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleHomeBanner} className="hidden" />
										</label>
									</div>
								)}

								{/* Empty state */}
								{!homeBannerPending && !homeBanner && (
									<label
										className="flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg cursor-pointer transition-colors mb-4"
										style={{
										margin: 0,
											height: '9rem',
											borderColor: homeBannerDragOver ? '#2563eb' : '#bfdbfe',
											backgroundColor: homeBannerDragOver ? '#eff6ff' : '#f8faff',
										}}
										onDragOver={e => e.preventDefault()}
										onDragEnter={e => { e.preventDefault(); setHomeBannerDragOver(true) }}
										onDragLeave={() => setHomeBannerDragOver(false)}
										onDrop={handleBannerDrop}
									>
										<span className="text-3xl font-thin text-blue-300 mb-1 leading-none">+</span>
										<span className="text-[11px] font-bold text-blue-400 uppercase tracking-widest">Uploader une bannière</span>
										<span className="text-[10px] text-slate-400 mt-1">1600 × 400 px</span>
										<input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleHomeBanner} className="hidden" />
									</label>
								)}

								{homeBannerError && <p className="text-xs text-red-500 mb-3">{homeBannerError}</p>}
								{homeBannerSuccess && <p className="text-xs text-emerald-600 font-medium mb-3">{homeBannerSuccess}</p>}

								<div className="flex gap-3">
									{homeBannerPending && (
										<button
											type="button"
											onClick={doSaveHomeBanner}
											disabled={homeBannerLoading}
											style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
											className="px-4 py-2 text-white text-xs font-bold rounded-lg disabled:opacity-50"
										>
											{homeBannerLoading ? 'Enregistrement...' : 'Valider la bannière'}
										</button>
									)}
									{homeBanner && !homeBannerPending && (
										<button
											type="button"
											onClick={doRemoveHomeBanner}
											disabled={homeBannerLoading}
											style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
											className="px-4 py-2 border border-red-200 text-red-500 text-xs font-bold rounded-lg disabled:opacity-50"
										>
											{homeBannerLoading ? 'Suppression...' : 'Supprimer'}
										</button>
									)}
								</div>
							</div>
						</section>

						{/* Card: Fonctionnalités Premium */}
						{isPremium && (
							<section className={cardClass}>
								<div className={cardHeaderClass}>
									<SparklesIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
									<div>
										<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-tight" style={{ margin: 0 }}>Fonctionnalités Premium</h2>
										<p className="text-sm text-slate-400" style={{ margin: "0.25rem 0 0" }}>Options avancées incluses dans votre formule Premium.</p>
									</div>
								</div>
								<div className="p-6 space-y-5">
									{/* Toggle messagerie */}
									<div className="flex items-center justify-between py-3 px-4 rounded-xl" style={{ backgroundColor: messagerieActive ? '#f0fdf4' : '#f8fafc', border: `1px solid ${messagerieActive ? '#bbf7d0' : '#e2e8f0'}` }}>
										<div className="flex items-start gap-3">
											<ChatAlt2Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: messagerieActive ? '#16a34a' : '#94a3b8' }} />
											<div>
												<p className="text-sm font-bold" style={{ margin: 0, color: messagerieActive ? '#166534' : '#64748b' }}>
													Messagerie
												</p>
												<p className="text-xs" style={{ margin: '0.2rem 0 0', color: messagerieActive ? '#15803d' : '#94a3b8' }}>
													{messagerieActive
														? 'Les campeurs peuvent échanger avec votre équipe via la messagerie intégrée.'
														: 'Activez la messagerie intégrée pour échanger avec les campeurs.'
													}
												</p>
											</div>
										</div>
										<Toggle checked={messagerieActive} onChange={() => {
											const newVal = !messagerieActive
											setMessagerieActive(newVal)
											const dc = props.dashboard_config || {}
											props.updateConfigDashboard({
												config_pms: props.config_pms ? props.config_pms.values : {},
												features: props.features || '',
												active_connexion: dc.active_connexion,
												application_ouverte: dc.application_ouverte,
												show_stay_dates: dc.show_stay_dates,
												show_stay_location: dc.show_stay_location,
												config_emails: dc.config_emails || {},
												due_billing: dc.due_billing || null,
												messages: { active: newVal }
											})
										}} />
									</div>
								</div>
							</section>
						)}

						{/* Card: Paiement en ligne (Stripe Connect) */}
						<Payments
							fetchStripeStatus={props.fetchStripeStatus}
							startStripeOnboarding={props.startStripeOnboarding}
							triggerStripePayout={props.triggerStripePayout}
						/>

						{/* Card: Horaires d'arrivée et de départ */}
						<section className={cardClass}>
							<div className={cardHeaderClass}>
								<ClockIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
								<div className="flex-1">
									<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-tight" style={{ margin: 0 }}>Horaires d'arrivée et de départ</h2>
									<p className="text-sm text-slate-400" style={{ margin: "0.25rem 0 0" }}>Utilisés par le pré-checkin (avertissements) et les interventions (ménages automatiques).</p>
								</div>
								{stayHoursState && (
									<span className={`text-xs font-bold flex-shrink-0 ${stayHoursState === 'error' ? 'text-red-500' : stayHoursState === 'saving' ? 'text-slate-400' : 'text-green-600'}`}>
										{stayHoursState === 'saving' ? 'Enregistrement…' : stayHoursState === 'error' ? 'Échec' : '✓ Enregistré'}
									</span>
								)}
							</div>
							<div className="p-6 space-y-4">
								{[
									{ group: 'locatif', label: 'Hébergements locatifs', hint: null },
									{ group: 'emplacement', label: 'Emplacements nus', hint: 'Laissez vide pour utiliser les horaires des locatifs.' },
								].map(({ group, label, hint }) => (
									<div key={group} className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
										<div>
											<p className="text-sm font-bold text-slate-800" style={{ margin: 0 }}>{label}</p>
											{hint && <p className="text-xs text-slate-400" style={{ margin: '0.2rem 0 0' }}>{hint}</p>}
										</div>
										<div className="flex items-center gap-4 flex-shrink-0">
											{[
												{ field: 'arrival', label: 'Arrivée à partir de' },
												{ field: 'departure', label: 'Départ avant' },
											].map(({ field, label: fieldLabel }) => (
												<label key={field} className="flex items-center gap-2">
													<span className="text-xs font-semibold text-slate-500">{fieldLabel}</span>
													<div className="flex items-center gap-1">
														<input
															type="number"
															min="0"
															max="23"
															value={stayHours[group][field]}
															onChange={e => handleStayHourChange(group, field, e.target.value)}
															placeholder={group === 'emplacement' ? (stayHours.locatif[field] || '—') : 'hh'}
															className="browser-default w-16 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 text-right focus:outline-none focus:border-blue-400 transition-all"
														/>
														<span className="text-xs font-bold text-slate-400">h</span>
													</div>
												</label>
											))}
										</div>
									</div>
								))}
								<div className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
									<div>
										<p className="text-sm font-bold text-slate-800" style={{ margin: 0 }}>Arrivée tardive</p>
										<p className="text-xs text-slate-400" style={{ margin: '0.2rem 0 0' }}>Heure à partir de laquelle une arrivée est considérée comme tardive (accueil fermé). Utilisée par le pré-checkin.</p>
									</div>
									<label className="flex items-center gap-2 flex-shrink-0">
										<span className="text-xs font-semibold text-slate-500">À partir de</span>
										<div className="flex items-center gap-1">
											<input
												type="number"
												min="0"
												max="23"
												value={stayHours.late_arrival}
												onChange={e => handleLateArrivalChange(e.target.value)}
												placeholder="19"
												className="browser-default w-16 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 text-right focus:outline-none focus:border-blue-400 transition-all"
											/>
											<span className="text-xs font-bold text-slate-400">h</span>
										</div>
									</label>
								</div>
							</div>
						</section>

						{/* Card: Espace de connexion */}
						<section className={cardClass}>
							<div className={cardHeaderClass}>
								<UserCircleIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
								<div>
									<h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-tight" style={{ margin: 0 }}>Espace de connexion</h2>
									<p className="text-sm text-slate-400" style={{ margin: "0.25rem 0 0" }}>Contrôlez l'accès à l'application et personnalisez l'écran affiché aux campeurs.</p>
								</div>
							</div>
							<div className="p-6 space-y-5">
								{/* Toggle accès */}
								<div className="flex items-center justify-between py-3 px-4 rounded-xl" style={{ backgroundColor: appOuverte ? '#f0fdf4' : '#eff6ff', border: `1px solid ${appOuverte ? '#bbf7d0' : '#bfdbfe'}` }}>
									<div>
										<p className="text-sm font-bold" style={{ margin: 0, color: appOuverte ? '#166534' : '#1e40af' }}>
											{appOuverte ? 'Accès libre' : 'Accès restreint'}
										</p>
										<p className="text-xs" style={{ margin: '0.2rem 0 0', color: appOuverte ? '#15803d' : '#3b82f6' }}>
											{appOuverte
												? 'Les campeurs accèdent librement, sans créer de compte.'
												: 'Les campeurs doivent créer un compte pour accéder à l\'application.'
											}
										</p>
									</div>
									<Toggle checked={appOuverte} onChange={() => {
										const newVal = !appOuverte
										setAppOuverte(newVal)
										const dc = props.dashboard_config || {}
										props.updateConfigDashboard({
											config_pms: props.config_pms ? props.config_pms.values : {},
											features: props.features || '',
											active_connexion: dc.active_connexion,
											application_ouverte: newVal,
											show_stay_dates: dc.show_stay_dates,
											show_stay_location: dc.show_stay_location,
											config_emails: dc.config_emails || {},
											due_billing: dc.due_billing || null
										})
									}} />
								</div>

								{/* Personnalisation — grisée si accès libre */}
								<div style={{ opacity: appOuverte ? 0.35 : 1, pointerEvents: appOuverte ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
									{/* Texte de bienvenue */}
									<div className="mb-5">
										<div className="flex items-center justify-between mb-1.5">
											<span className={labelClass} style={{ marginBottom: 0 }}>Texte de bienvenue</span>
											<div className="relative inline-flex items-center">
												<img
													src={currentLangMeta.src}
													alt={currentLangMeta.label}
													className="w-4 h-3 object-cover rounded-sm absolute left-2.5 z-10 pointer-events-none"
												/>
												<select
													value={currentLang}
													onChange={e => setCurrentLang(e.target.value)}
													className="browser-default pl-8 pr-4 py-1.5 text-xs font-bold border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 text-slate-700"
												>
													{LANGS_META.map(l => (
														<option key={l.code} value={l.code}>{l.label}</option>
													))}
												</select>
											</div>
										</div>
										<textarea
											className="browser-default w-full border border-slate-200 rounded-lg px-4 py-3 text-sm bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all resize-none"
											rows={3}
											value={getCurrentWelcome()}
											onChange={e => handleWelcome(e.target.value)}
											placeholder={`Bienvenue en ${currentLangMeta.label}...`}
											maxLength={LENGTH_MAX + 10}
										/>
										<div className="flex justify-between mt-1">
											{errors.welcome
												? <span className="text-[11px] text-red-500 font-medium">{errors.welcome}</span>
												: <span />
											}
											<span className={`text-[11px] ${getCurrentWelcome().length > LENGTH_MAX ? 'text-red-500' : 'text-slate-300'}`}>
												{getCurrentWelcome().length}/{LENGTH_MAX}
											</span>
										</div>
									</div>

									{/* Fond d'écran */}
									<div>
										<span className={labelClass}>Fond d'écran de connexion</span>
										<label className="flex gap-3 items-center cursor-pointer group" style={{ margin: 0 }}>
											<input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleBackground} className="hidden" />
											<div className="w-16 h-12 flex-shrink-0 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden group-hover:border-blue-300 transition-colors">
												{background
													? <img src={background} alt="fond d'écran" className="w-full h-full object-cover" />
													: <PhotographIcon className="w-6 h-6 text-slate-300" />
												}
											</div>
											<div className="flex-1">
												<div className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs text-slate-400 bg-slate-50 pointer-events-none">
													{background ? 'Image sélectionnée — cliquer pour changer' : "URL de l'image (ou upload via l'icône)"}
												</div>
												<p className="text-[10px] text-slate-400 italic mt-1.5">
													S'affiche sur la page de connexion des applications nécessitant un compte.
												</p>
											</div>
										</label>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</form>
		</div>
	)
}

export default ConfigWebapp
